import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";
const BLANK = { title: "", description: "", price: "", sizes: "", image: "", active: true, sort_order: 0 };

/** Resize + re-encode an uploaded photo to a compact JPEG data URL (max 1200px). */
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file is not an image."));
      img.onload = () => {
        const scale = Math.min(1, 1200 / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export default function AdminBulkPieces() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [pieces, setPieces] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const headers = { "X-Admin-Key": adminKey };

  const load = async (key = adminKey) => {
    try {
      const r = await api.get("/bulk-pieces/all", { headers: { "X-Admin-Key": key } });
      setPieces(r.data.pieces || []);
      localStorage.setItem(KEY_STORE, key);
    } catch {
      setPieces(null);
      if (key) toast.error("Invalid admin key.");
    }
  };

  useEffect(() => { if (adminKey) load(adminKey); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setField("image", await compressImage(file));
      toast.success("Photo ready.");
    } catch (err) { toast.error(err.message); }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      title: p.title, description: p.description || "",
      price: p.price ?? "", sizes: (p.sizes || []).join(", "),
      image: p.image || "", active: p.active !== false, sort_order: p.sort_order || 0,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => { setForm(BLANK); setEditId(null); setShowForm(false); };

  const save = async () => {
    if (!adminKey) { toast.error("Enter your admin key first."); return; }
    if (!form.title.trim()) { toast.error("Give the piece a title."); return; }
    if (!form.image) { toast.error("Add a photo or an image URL."); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      price: form.price === "" ? null : Number(form.price),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      image: form.image,
      active: form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (editId) await api.put(`/bulk-pieces/${editId}`, payload, { headers });
      else await api.post("/bulk-pieces", payload, { headers });
      toast.success(editId ? "Piece updated." : "Piece added — it's live on the bridesmaid page.");
      reset();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Try again.");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/bulk-pieces/${p.id}`, { headers });
      toast.success("Piece deleted.");
      load();
    } catch { toast.error("Could not delete. Try again."); }
  };

  const inputCls = "w-full border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A]";

  return (
    <main className="max-w-[900px] mx-auto px-5 sm:px-8 py-14 min-h-[70vh]" data-testid="admin-bulk-pieces-page">
      <Seo title="Bridesmaid Pieces Admin" path="/admin/bulk-pieces" noindex />
      <div className="overline text-[#B84A3B] mb-2">Store Admin</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Bridesmaid Pre-Order Pieces</h1>
      <p className="text-sm text-[#6E6E69] mb-2 font-body">
        Add the special-order dresses shown on the <a href="/bridesmaid-dresses" className="underline">bridesmaid page</a>. Customers order these via WhatsApp.
      </p>
      <p className="text-xs text-[#6E6E69] mb-8 font-body">
        Tip: pieces you add to the Shopify collection <b>"Pre Order Bridesmaid Dresses"</b> also appear on the page automatically — manage those directly in Shopify (they stay hidden from the main shop).
      </p>

      <label className="block mb-8 max-w-sm">
        <span className="overline text-[#1A1A1A] block mb-1.5">Admin Key</span>
        <div className="flex gap-2">
          <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Enter admin key"
            data-testid="admin-key-input" className={inputCls} />
          <button onClick={() => load()} data-testid="admin-load-button"
            className="px-4 border border-[#1A1A1A] text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">Load</button>
        </div>
      </label>

      {pieces !== null && (
        <>
          <button onClick={() => (showForm ? reset() : setShowForm(true))} data-testid="toggle-form-button"
            className="inline-flex items-center gap-2 mb-8 bg-[#1A1A1A] text-white px-6 py-3 text-[0.68rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] transition-colors">
            {showForm ? <><X size={13} /> Cancel</> : <><Plus size={13} /> Add a Piece</>}
          </button>

          {showForm && (
            <div className="border border-[#E5E5DF] p-5 sm:p-7 mb-10" data-testid="piece-form">
              <h2 className="font-display text-2xl mb-5">{editId ? "Edit Piece" : "New Piece"}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="sm:col-span-2"><span className="overline text-[#1A1A1A] block mb-1.5">Title *</span>
                  <input value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Rose Anarkali Gown" data-testid="piece-title-input" className={inputCls} /></label>
                <label><span className="overline text-[#1A1A1A] block mb-1.5">Price (USD, optional)</span>
                  <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setField("price", e.target.value)} placeholder="89" data-testid="piece-price-input" className={inputCls} /></label>
                <label><span className="overline text-[#1A1A1A] block mb-1.5">Sort order</span>
                  <input type="number" value={form.sort_order} onChange={(e) => setField("sort_order", e.target.value)} data-testid="piece-sort-input" className={inputCls} /></label>
                <label className="sm:col-span-2"><span className="overline text-[#1A1A1A] block mb-1.5">Sizes (comma separated)</span>
                  <input value={form.sizes} onChange={(e) => setField("sizes", e.target.value)} placeholder="XS, S, M, L, XL, XXL" data-testid="piece-sizes-input" className={inputCls} /></label>
                <label className="sm:col-span-2"><span className="overline text-[#1A1A1A] block mb-1.5">Description</span>
                  <textarea rows={2} value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Fabric, colours available, work details…" data-testid="piece-description-input" className={inputCls} /></label>

                <div className="sm:col-span-2">
                  <span className="overline text-[#1A1A1A] block mb-1.5">Photo *</span>
                  <div className="flex flex-wrap items-start gap-4">
                    <label className="cursor-pointer border border-dashed border-[#B84A3B] text-[#B84A3B] px-5 py-3 text-[0.68rem] font-mono tracking-[0.15em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors" data-testid="piece-image-upload">
                      Upload photo
                      <input type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="piece-image-file" />
                    </label>
                    <input value={form.image.startsWith("data:") ? "" : form.image} onChange={(e) => setField("image", e.target.value)}
                      placeholder="…or paste an image URL" data-testid="piece-image-url-input" className={`${inputCls} flex-1 min-w-[220px]`} />
                    {form.image && <img src={form.image} alt="Preview" data-testid="piece-image-preview" className="w-20 h-24 object-cover border border-[#E5E5DF]" />}
                  </div>
                </div>

                <label className="flex items-center gap-3 cursor-pointer" data-testid="piece-active-toggle">
                  <input type="checkbox" checked={form.active} onChange={(e) => setField("active", e.target.checked)} className="w-5 h-5 accent-[#B84A3B]" />
                  <span className="font-body text-sm">Show on the bridesmaid page</span>
                </label>
              </div>
              <button onClick={save} disabled={saving} data-testid="piece-save-button"
                className="mt-6 bg-[#1A1A1A] text-white px-10 py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-60">
                {saving ? "Saving…" : editId ? "Save Changes" : "Add Piece"}
              </button>
            </div>
          )}

          {pieces.length === 0 ? (
            <p className="text-sm text-[#6E6E69] font-body" data-testid="pieces-empty">No pieces yet — add your first bridesmaid style above.</p>
          ) : (
            <div className="space-y-3" data-testid="pieces-list">
              {pieces.map((p) => (
                <div key={p.id} className="flex items-center gap-4 border border-[#E5E5DF] p-3" data-testid={`admin-piece-${p.id}`}>
                  <img src={p.image} alt={p.title} className="w-14 h-[4.25rem] object-cover bg-[#EFEFEA] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-sm font-semibold truncate">{p.title}</div>
                    <div className="text-xs text-[#6E6E69] font-body mt-0.5">
                      {p.price != null ? money(p.price) : "No price"} · {(p.sizes || []).join(", ") || "no sizes"}
                      {p.active === false && <span className="ml-2 text-[#B84A3B] font-semibold">· hidden</span>}
                    </div>
                  </div>
                  <button onClick={() => startEdit(p)} data-testid={`edit-${p.id}`} aria-label={`Edit ${p.title}`}
                    className="p-2.5 border border-[#E5E5DF] hover:border-[#1A1A1A] transition-colors"><Pencil size={14} /></button>
                  <button onClick={() => remove(p)} data-testid={`delete-${p.id}`} aria-label={`Delete ${p.title}`}
                    className="p-2.5 border border-[#E5E5DF] text-[#B84A3B] hover:border-[#B84A3B] transition-colors"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </main>
  );
}
